import { useEffect, useState } from 'react';
import {
  clearRealtimeDebug,
  type RealtimeDebugEntry,
  subscribeRealtimeDebug,
} from '@/lib/realtime-debug';

/**
 * Bottom-right debug panel that shows realtime WS traffic as it
 * happens. Opt-in: render only when the URL has `?debug=1` (or any
 * truthy value) so it stays out of the way of real users.
 *
 * Click an entry to expand its payload.
 */
export function RealtimeDebugOverlay() {
  const [entries, setEntries] = useState<readonly RealtimeDebugEntry[]>([]);
  const [open, setOpen] = useState(true);
  const [expanded, setExpanded] = useState<number | null>(null);

  useEffect(() => subscribeRealtimeDebug(setEntries), []);

  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  if (!params.get('debug')) return null;

  return (
    <div
      style={{
        position: 'fixed',
        right: 8,
        bottom: 8,
        zIndex: 9999,
        width: 360,
        maxHeight: '60vh',
        background: 'rgba(20, 20, 20, 0.92)',
        color: '#e7e7e7',
        font: '11px ui-monospace, SFMono-Regular, Menlo, monospace',
        borderRadius: 8,
        border: '1px solid #444',
        boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          padding: '6px 10px',
          borderBottom: '1px solid #333',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: '#1a1a1a',
          borderRadius: '8px 8px 0 0',
        }}
      >
        <strong style={{ flex: 1 }}>realtime · {entries.length}</strong>
        <button
          type="button"
          onClick={clearRealtimeDebug}
          style={debugBtnStyle}
          title="Clear"
        >
          clear
        </button>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          style={debugBtnStyle}
          title={open ? 'Collapse' : 'Expand'}
        >
          {open ? '–' : '+'}
        </button>
      </div>
      {open && (
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {entries.length === 0 && (
            <div style={{ padding: 12, color: '#888' }}>(no traffic yet)</div>
          )}
          {entries.map((e) => {
            const isExpanded = expanded === e.id;
            const arrow =
              e.direction === 'in' ? '←' : e.direction === 'out' ? '→' : '·';
            const color =
              e.direction === 'in' ? '#7fd' : e.direction === 'out' ? '#fc7' : '#888';
            return (
              <div
                key={e.id}
                onClick={() => setExpanded(isExpanded ? null : e.id)}
                style={{
                  padding: '4px 10px',
                  borderBottom: '1px solid #222',
                  cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
                  <span style={{ color: '#888' }}>{formatTime(e.at)}</span>
                  <span style={{ color, width: 12 }}>{arrow}</span>
                  <span style={{ color: '#fff', flex: 1 }}>{e.type}</span>
                  <span style={{ color: '#888', fontSize: 10 }}>{shortChannel(e.channel)}</span>
                </div>
                {isExpanded && e.payload !== undefined && (
                  <pre
                    style={{
                      margin: '4px 0 0',
                      padding: 6,
                      background: '#0c0c0c',
                      borderRadius: 4,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      color: '#cbd',
                      maxHeight: 260,
                      overflowY: 'auto',
                    }}
                  >
                    {safeStringify(e.payload)}
                  </pre>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const debugBtnStyle: React.CSSProperties = {
  background: '#2a2a2a',
  color: '#ddd',
  border: '1px solid #444',
  borderRadius: 4,
  padding: '2px 6px',
  font: 'inherit',
  cursor: 'pointer',
};

function formatTime(ms: number) {
  const d = new Date(ms);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${String(
    d.getMilliseconds(),
  ).padStart(3, '0')}`;
}
const pad = (n: number) => String(n).padStart(2, '0');

function shortChannel(c: string) {
  // /play/<uuid>/players → players, etc.
  const m = c.match(/\/play\/[^/]+\/(.+)$/);
  return m ? m[1] : c;
}

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}
